import React from 'react';
import logoUrl from './assets/storsafe-logo.png';
import './LandingPage.css';

interface Props {
  onLogin:    () => void;
  onRegister: () => void;
  onPricing:  () => void;
}

export default function LandingPage({ onLogin, onRegister, onPricing }: Props) {
  return (
    <div className="lp-root">

      {/* ── 1. NAVBAR ── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <img src={logoUrl} alt="StorSafe" className="lp-nav-logo" />
          <div className="lp-nav-links">
            <button className="lp-nav-link" onClick={onPricing}>Precios</button>
            <button className="lp-btn-ghost" onClick={onLogin}>Iniciar sesión</button>
            <button className="lp-btn-primary" onClick={onRegister}>Prueba gratis</button>
          </div>
        </div>
      </nav>

      {/* ── 2. HERO ── */}
      <section className="lp-hero">
        <div className="lp-grid-overlay" />
        <div className="lp-hero-inner">
          <div className="lp-badge">
            <ShieldIcon />
            Gestión de Bodegas Químicas para Ecuador
          </div>
          <h1 className="lp-hero-title">
            Gestión profesional de
            <br />
            <span className="lp-hero-accent">bodegas químicas</span>
          </h1>
          <p className="lp-hero-sub">
            Cumple la INEN&nbsp;2266 desde el primer día. Gestiona tus productos químicos, compatibilidad SGA,
            hojas de seguridad y planes de emergencia ERG 2024 en un solo lugar.
          </p>
          <div className="lp-hero-actions">
            <button className="lp-btn-primary lp-btn-lg" onClick={onRegister}>
              Empieza gratis 5 días
            </button>
          </div>
          <p className="lp-hero-fine">Sin tarjeta de crédito · Trial 5 días gratis</p>
        </div>
      </section>

      {/* ── 3. BARRA DE NORMATIVA ── */}
      <div className="lp-trust">
        <div className="lp-trust-inner">
          {['INEN 2266', 'IMDG/ONU', 'ERG 2024', 'SGA/GHS', 'NFPA 704', 'LOPDP'].map((item) => (
            <div key={item} className="lp-trust-item">
              <CheckIcon />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 4. PROBLEMA ── */}
      <section className="lp-problem">
        <div className="lp-section-inner">
          <h2 className="lp-section-title">
            Un error de compatibilidad química puede cerrar tu empresa
          </h2>
          <p className="lp-section-sub">
            Las empresas que manejan productos químicos en Ecuador enfrentan riesgos reales
            cada día. Sin las herramientas correctas, el cumplimiento es imposible.
          </p>
          <div className="lp-cards">
            <div className="lp-card">
              <div className="lp-card-icon lp-card-icon--red">
                <WarningIcon />
              </div>
              <h3>Incompatibilidades ocultas</h3>
              <p>
                Productos que parecen inofensivos pueden generar reacciones peligrosas
                si no se almacenan correctamente. Sin un sistema, es imposible saberlo.
              </p>
            </div>
            <div className="lp-card">
              <div className="lp-card-icon lp-card-icon--yellow">
                <ClockIcon />
              </div>
              <h3>Reportes manuales lentos</h3>
              <p>
                Generar una matriz de compatibilidad manualmente toma días de trabajo.
                Cada actualización del inventario significa empezar de cero.
              </p>
            </div>
            <div className="lp-card">
              <div className="lp-card-icon lp-card-icon--orange">
                <FlameIcon />
              </div>
              <h3>Emergencias sin protocolo</h3>
              <p>
                Sin un plan de emergencia actualizado por producto, en una crisis tu
                equipo no sabe qué hacer. Las consecuencias pueden ser irreversibles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. SOLUCIÓN ── */}
      <section className="lp-solution">
        <div className="lp-section-inner">
          <h2 className="lp-section-title lp-section-title--light">
            StorSafe hace en segundos lo que antes tomaba días
          </h2>
          <div className="lp-features">
            <div className="lp-feature">
              <div className="lp-feature-icon">
                <MatrixIcon />
              </div>
              <div className="lp-feature-text">
                <h3>Matriz de compatibilidad automática</h3>
                <p>
                  Detecta incompatibilidades INEN 2266 en tiempo real. Cada vez que agregas
                  un producto, la matriz se actualiza sola.
                </p>
              </div>
            </div>
            <div className="lp-feature">
              <div className="lp-feature-icon">
                <PdfIcon />
              </div>
              <div className="lp-feature-text">
                <h3>Reportes PDF de cumplimiento</h3>
                <p>
                  Genera reportes listos para inspección en un clic. Formato oficial,
                  con tu logo y datos de empresa.
                </p>
              </div>
            </div>
            <div className="lp-feature">
              <div className="lp-feature-icon">
                <EmergPlanIcon />
              </div>
              <div className="lp-feature-text">
                <h3>Plan de emergencia ERG 2024</h3>
                <p>
                  Protocolos de emergencia por sustancia, basados en la guía ERG 2024.
                  Imprimible y actualizado automáticamente.
                </p>
              </div>
            </div>
            <div className="lp-feature">
              <div className="lp-feature-icon">
                <UsersIcon />
              </div>
              <div className="lp-feature-text">
                <h3>Multi-usuario y multi-bodega</h3>
                <p>
                  Gestiona varias bodegas y equipos desde una sola cuenta. Roles
                  diferenciados: administrador, gestor, visualizador.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. MÓDULOS ── */}
      <section className="lp-modules">
        <div className="lp-section-inner">
          <h2 className="lp-section-title">Todo lo que necesitas para cumplir la ley</h2>
          <p className="lp-section-sub">
            Desde el inventario de químicos hasta los reportes de cumplimiento — un solo sistema, sin hojas de cálculo.
          </p>
          <div className="lp-modules-grid">
            <ModuleCard
              icon={<ChemIcon />}
              color="#22c55e"
              title="Gestión de Químicos"
              desc="Controla tu inventario de sustancias peligrosas y cumple la normativa INEN 2266 sin complicaciones."
            />
            <ModuleCard
              icon={<MatrixIcon />}
              color="#38bdf8"
              title="Matriz de Compatibilidad"
              desc="Detecta incompatibilidades entre productos químicos en tiempo real, según la norma INEN 2266."
            />
            <ModuleCard
              icon={<PdfIcon />}
              color="#f59e0b"
              title="Hojas de Seguridad SDS"
              desc="Carga y consulta las hojas de seguridad de cada producto desde un solo repositorio centralizado."
            />
            <ModuleCard
              icon={<ChemIcon />}
              color="#a78bfa"
              title="Etiquetado SGA"
              desc="Genera etiquetas de productos químicos según el Sistema Globalmente Armonizado (SGA/GHS)."
            />
            <ModuleCard
              icon={<UsersIcon />}
              color="#3b82f6"
              title="Multi-bodega y Usuarios"
              desc="Gestiona varias bodegas y equipos desde una sola cuenta, con roles diferenciados por usuario."
            />
            <ModuleCard
              icon={<EmergPlanIcon />}
              color="#22c55e"
              title="Planes de Emergencia ERG 2024"
              desc="Protocolos de emergencia por sustancia, basados en la guía ERG 2024, listos para imprimir."
            />
            <ModuleCard
              icon={<PdfIcon />}
              color="#2FBF71"
              title="Reportes y Cumplimiento"
              desc="Genera documentos legales listos para presentar ante cualquier autoridad competente."
            />
            <ModuleCard
              icon={<EmergPlanIcon />}
              color="#64748b"
              title="SafeRespond"
              desc="Herramienta de respuesta ante emergencias para equipos de primera respuesta."
              badge="Próximamente"
            />
          </div>
        </div>
      </section>

      {/* ── 7. NORMATIVA ── */}
      <section className="lp-normativa">
        <div className="lp-section-inner">
          <h2 className="lp-section-title lp-section-title--light">Normativa que cumples</h2>
          <p className="lp-section-sub" style={{ color: '#64748b' }}>
            StorSafe está diseñado desde el primer día para cumplir la regulación ecuatoriana e internacional.
          </p>
          <div className="lp-norm-cols">
            <NormCol
              title="Sustancias peligrosas"
              icon="🧪"
              items={['INEN 2266', 'IMDG / ONU', 'ERG 2024', 'SGA / GHS']}
            />
            <NormCol
              title="Almacenamiento seguro"
              icon="🏭"
              items={['NFPA 704', 'ERG 2024', 'Almacenamiento INEN']}
            />
            <NormCol
              title="Gestión documental"
              icon="📋"
              items={['LOPDP Ecuador', 'ISO 11014 (SDS)', 'Trazabilidad de inventario']}
            />
          </div>
        </div>
      </section>

      {/* ── 8. CTA FINAL ── */}
      <section className="lp-cta">
        <div className="lp-grid-overlay" />
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">¿Listo para cumplir la INEN 2266?</h2>
          <p className="lp-cta-sub">5 días gratis. Sin tarjeta. Sin compromiso.</p>
          <button className="lp-btn-primary lp-btn-lg" onClick={onRegister}>
            Comenzar prueba gratuita
          </button>
          <p className="lp-cta-prices">
            Starter desde $100/mes · Professional desde $200/mes · Enterprise desde $500/mes
          </p>
        </div>
      </section>

      {/* ── 7. FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <p className="lp-footer-copy">© 2026 StorSafe · AndeanSoft SAS · Ecuador</p>
          <div className="lp-footer-links">
            <button className="lp-footer-link-btn" onClick={() => window.location.href = '/terminos-y-condiciones'}>
              Términos
            </button>
            <button className="lp-footer-link-btn" onClick={() => window.location.href = '/politica-de-pagos'}>
              Política de pagos
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 01-7 7 7 7 0 01-7-7c0-1.53.4-2.973 1.1-4.2A4.5 4.5 0 008.5 14.5z" />
    </svg>
  );
}

function MatrixIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function EmergPlanIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function ChemIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3v11.5a3.5 3.5 0 007 0V3" />
      <line x1="6" y1="3" x2="18" y2="3" />
      <path d="M6 17.5c0 1.93 1.57 3.5 3.5 3.5h5a3.5 3.5 0 000-7H6" />
    </svg>
  );
}

// ── Module card ────────────────────────────────────────────────────────────────

function ModuleCard({ icon, color, title, desc, badge }: {
  icon: React.ReactNode; color: string; title: string; desc: string; badge?: string;
}) {
  return (
    <div className="lp-module-card">
      <div className="lp-module-icon" style={{ background: color + '18', color }}>
        {icon}
      </div>
      <div className="lp-module-body">
        <div className="lp-module-title-row">
          <h3 className="lp-module-title">{title}</h3>
          {badge && <span className="lp-module-badge">{badge}</span>}
        </div>
        <p className="lp-module-desc">{desc}</p>
      </div>
    </div>
  );
}

// ── Normativa column ───────────────────────────────────────────────────────────

function NormCol({ title, icon, items }: { title: string; icon: string; items: string[] }) {
  return (
    <div className="lp-norm-col">
      <div className="lp-norm-col-header">
        <span className="lp-norm-icon">{icon}</span>
        <h3 className="lp-norm-title">{title}</h3>
      </div>
      <ul className="lp-norm-list">
        {items.map(item => (
          <li key={item} className="lp-norm-item">
            <CheckIcon />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
